import React from 'react';
import Icon from '../../../components/AppIcon';

/**
 * 🎯 ROTATION HEADER - Compact & Info-Dense
 * 
 * Mobile: Single column, essential info only
 * Desktop: Spread out, more detail visible
 * 
 * Shows:
 * - Rotation number (big and bold)
 * - Employee name + position
 * - Aircraft + base
 * - Date range
 * - Compliance status badge
 * - THREE-ID metadata (rotation_id, employee_id) - for debugging
 */

const RotationHeader = ({ rotation, summary, compliance, onBack, onRefresh }) => {
  const isCompliant = compliance?.is_compliant || compliance?.overall_status === 'legal';
  
  const formatDateRange = () => {
    if (!summary?.dates?.start) return 'N/A';
    
    const start = new Date(summary.dates.start);
    const end = summary.dates.end ? new Date(summary.dates.end) : null;
    
    const startStr = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const endStr = end ? end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '?';
    
    return `${startStr} → ${endStr}`;
  };

  return (
    <div className="card p-4 md:p-6">
      {/* Top Row: Back button + Rotation Number + Actions */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          {/* Mobile: Icon only, Desktop: Icon + Text */}
          <button
            onClick={onBack}
            className="btn btn-ghost btn-sm"
            title="Back to Data Management"
          >
            <Icon name="ArrowLeft" className="w-5 h-5" />
            <span className="hidden md:inline ml-2">Back</span>
          </button>
          
          {/* Rotation Number - BIG and BOLD */}
          <div>
            <div className="text-sm text-muted-foreground">Rotation</div>
            <div className="text-3xl md:text-4xl font-bold text-foreground">
              #{summary?.rotation_number || 'N/A'}
            </div>
          </div>
        </div>

        {/* Actions: Refresh */}
        <button
          onClick={onRefresh}
          className="btn btn-ghost btn-sm"
          title="Refresh data"
        >
          <Icon name="RefreshCw" className="w-5 h-5" />
        </button>
      </div>

      {/* Employee Info + Aircraft + Dates */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        {/* Employee */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
            <Icon name="User" className="w-5 h-5 text-primary" />
          </div>
          <div className="min-w-0">
            <div className="text-xs text-muted-foreground">Pilot</div>
            <div className="font-semibold text-foreground truncate">
              {summary?.employee_name || 'Unknown'}
            </div>
            <div className="text-sm text-muted-foreground">
              {summary?.position || 'N/A'} • {summary?.base || 'N/A'}
            </div>
          </div>
        </div>

        {/* Aircraft */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-accent-cyan/20 flex items-center justify-center flex-shrink-0">
            <Icon name="Plane" className="w-5 h-5 text-accent-cyan" />
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Aircraft</div>
            <div className="font-semibold text-foreground">
              {summary?.aircraft || 'N/A'}
            </div>
            <div className="text-sm text-muted-foreground">
              {summary?.totals?.flight_count || 0} flights
            </div>
          </div>
        </div>

        {/* Dates */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-warning/20 flex items-center justify-center flex-shrink-0">
            <Icon name="Calendar" className="w-5 h-5 text-warning" />
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Duration</div>
            <div className="font-semibold text-foreground">
              {formatDateRange()}
            </div>
            <div className="text-sm text-muted-foreground">
              {summary?.dates?.duration_days || 0} days • TAFB: {summary?.totals?.tafb_hours || 0}h
            </div>
          </div>
        </div>
      </div>

      {/* Compliance Badge + THREE-ID Debug Info */}
      <div className="flex items-center justify-between pt-4 border-t border-border">
        {/* Compliance Status */}
        <div className="flex items-center gap-2">
          {isCompliant ? (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-success/20 text-success">
              <Icon name="CheckCircle2" className="w-4 h-4" />
              <span className="text-sm font-medium">FAR 117 Legal</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-error/20 text-error">
              <Icon name="AlertCircle" className="w-4 h-4" />
              <span className="text-sm font-medium">
                {compliance?.violations?.length || 0} Violations
              </span>
            </div>
          )}
        </div>

        {/* THREE-ID Debug (hidden on mobile, visible on desktop for developers) */}
        <div className="hidden lg:flex items-center gap-3 text-xs text-muted-foreground">
          <div title="Rotation ID (Trace ID)">
            <Icon name="Hash" className="w-3 h-3 inline mr-1" />
            {rotation?.rotation_id?.substring(0, 8)}...
          </div>
          <div title="Employee ID">
            <Icon name="User" className="w-3 h-3 inline mr-1" />
            {rotation?.employee_id}
          </div>
          <div title="Compiled Rotation ID">
            <Icon name="Database" className="w-3 h-3 inline mr-1" />
            {rotation?.compiled_rotation_id?.substring(0, 8)}...
          </div>
        </div>
      </div>
    </div>
  );
};

export default RotationHeader;
